import React, { useEffect, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { styled } from '../../../../stitches.config'
import { faTag } from '@fortawesome/free-solid-svg-icons/faTag'
import handleEnterPressEvent from '@/src/common/utilities/handleEnterPressEvent'

interface CopyPromoCodeProps {
    promoCode: string
    style?: object
    isCopiedExternally?: boolean
    setIsCopiedExternally?: Function
}

const blue = 'rgb(44, 115, 255)'

const CopyPromoCodeWrapper = styled('div', {
    p: '.5rem',
    border: 'dotted',
    borderColor: '$secondary',
    cursor: 'pointer',
    transition: 'all .25s ease-out',
    textAlign: 'center',
    '&:hover': {
        background: '$secondaryLight',
    },
    '&.copied': {
        background: '$successGreenLight',
        borderColor: '$success',
        color: '$success',
    },
})

const TagIcon = styled(FontAwesomeIcon, {
    marginRight: '6px',
    color: '$primary',
})

const PromoCode = styled('span', {
    color: blue,
    '&.copied': {
        color: '$success',
    },
})

const CopySuccess = styled('div', {
    color: '$success',
    fontSize: '.8rem',
    fontWeight: 'bold',
    marginTop: '.8rem',
    textAlign: 'center',
})

function copyCode(promoCode: string, setCopied: Function) {
    navigator.clipboard.writeText(promoCode).catch((e) => {
        console.error(e)
    })

    setCopied(true)
}

const CopyPromoCodeButton = ({
    promoCode,
    style = {},
    isCopiedExternally = false,
    setIsCopiedExternally,
}: CopyPromoCodeProps) => {
    const [copied, setCopied] = useState<boolean>(false)

    useEffect(() => {
        if (isCopiedExternally && setIsCopiedExternally) {
            copyCode(promoCode, setCopied)
            setIsCopiedExternally(false)
        }
    }, [isCopiedExternally, promoCode, setIsCopiedExternally])

    useEffect(() => {
        const timeout = setTimeout(() => {
            if (copied) setCopied(false)
        }, 5000)

        return () => clearTimeout(timeout)
    }, [copied])

    return (
        <div style={style}>
            <div style={{ textAlign: 'center', marginBottom: '.5rem' }}>
                <TagIcon icon={faTag} />
                Promo Code:{' '}
            </div>
            <CopyPromoCodeWrapper
                onClick={() => copyCode(promoCode, setCopied)}
                onKeyDown={(e) =>
                    handleEnterPressEvent(e, () =>
                        copyCode(promoCode, setCopied)
                    )
                }
                className={copied ? 'copied' : ''}
                tabIndex={0}
            >
                <PromoCode className={copied ? 'copied' : ''}>
                    {promoCode}
                </PromoCode>
            </CopyPromoCodeWrapper>
            {copied && (
                <CopySuccess>Promo code successfully copied!</CopySuccess>
            )}
        </div>
    )
}

export default CopyPromoCodeButton
